import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Play } from "lucide-react";
import { OUTCOME_LABELS } from "../CallOutcomeDialog";

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

const Stat = ({ label, value, id }) => (
  <Card className="p-5 bg-card border-border/70">
    <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="text-2xl font-display font-bold mt-1" data-testid={`call-stat-${id}`}>{value}</p>
  </Card>
);

const playRecording = async (id) => {
  const { data } = await api.get(`/calling/calls/${id}/recording`, { responseType: "blob" });
  new Audio(URL.createObjectURL(data)).play();
};

export const CallLogTab = ({ subId }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [calls, setCalls] = useState([]);
  useEffect(() => {
    api.get(`/calling/stats?sub_account_id=${subId}`).then(({ data }) => setStats(data));
    api.get(`/calling/calls?sub_account_id=${subId}`).then(({ data }) => setCalls(data));
  }, [subId]);

  return (
    <div className="space-y-6">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat id="total" label="Calls (30d)" value={stats.total} />
          <Stat id="connect-rate" label="Connect rate" value={`${stats.connect_rate}%`} />
          <Stat id="talk-time" label="Talk time" value={fmt(stats.talk_seconds)} />
          <Stat id="demos" label="Demos booked" value={stats.demos} />
        </div>
      )}
      {stats?.by_user.length > 0 && (
        <Card className="p-6 bg-card border-border/70">
          <h3 className="font-display font-semibold mb-3">By team member</h3>
          {stats.by_user.map((u) => (
            <div key={u.name} className="flex justify-between text-sm py-1.5 border-b border-border/40 last:border-0">
              <span>{u.name}</span><span className="text-muted-foreground">{u.calls} calls · {u.connected} connected · {u.demos} demos</span>
            </div>
          ))}
        </Card>
      )}
      <Card className="p-6 bg-card border-border/70">
        <h3 className="font-display font-semibold mb-3">Call log</h3>
        <div className="space-y-1" data-testid="call-log">
          {calls.map((c) => (
            <div key={c.id} className="flex flex-wrap items-center gap-3 text-sm py-2 border-b border-border/40 last:border-0" data-testid={`call-row-${c.id}`}>
              <button className="font-medium hover:text-primary flex-1 text-left min-w-[140px]" onClick={() => navigate(`/contacts/${c.contact_id}`)}>{c.contact_name || c.to_number || c.from_number}</button>
              <span className="text-muted-foreground">{c.direction === "inbound" ? "Inbound" : `Out · ${c.user_name || ""}`}</span>
              <span className={c.status === "missed" ? "text-destructive" : "text-muted-foreground"}>{c.outcome ? OUTCOME_LABELS[c.outcome] : c.status}</span>
              <span className="text-muted-foreground font-mono">{fmt(c.duration || 0)}</span>
              <span className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleString()}</span>
              {c.has_recording && <Button size="sm" variant="ghost" onClick={() => playRecording(c.id)} data-testid={`call-play-${c.id}`}><Play className="w-4 h-4" /></Button>}
            </div>
          ))}
          {calls.length === 0 && <p className="text-sm text-muted-foreground">No calls yet. Open any contact and press Call.</p>}
        </div>
      </Card>
    </div>
  );
};
